import React, { useState, useEffect, useRef } from 'react';
import * as Yup from 'yup';
import { toast } from 'react-toastify';
import useI18n from '@/hooks/useI18n';
import { FormDataForCreateCampaign } from '@/types/interfaces';
import { Button } from '../Button';
import CustomRadioButton from '../CustomRadioButton';
import { COUNTRIES } from '@/config/countryData';
import { useFetchLocationQuery } from '@/hooks/queries/useUserQueries';
import { useDebounce } from '@/utils/useDebounce';
import ToolTip from '../ToolTip';

interface CreateCampaignStep4Props {
  formData: FormDataForCreateCampaign;
  handleChange: (newValues: Partial<FormDataForCreateCampaign>) => void;
  onNext: () => void;
  onPrevious: () => void;
}

interface LocationData {
  name: string;
  address: string;
  location?: {
    lat: number;
    lng: number;
  };
}

interface DisplayItem {
  displayName: string;
  displayAddress: string;
  backendValue: string;
}

const parseLocationData = (data: string): LocationData => {
  const parts = data.split(',').map((part) => part.trim());
  let name = '';
  let address = data;

  if (parts.length > 1) {
    name = parts[0];
    address = data;
  } else {
    name = data;
  }

  return { name, address };
};

const CreateCampaignStep4: React.FC<CreateCampaignStep4Props> = ({
  formData,
  handleChange,
  onNext,
  onPrevious,
}) => {
  const [searchValue, setSearchValue] = useState<string>('');
  const [displayItems, setDisplayItems] = useState<DisplayItem[]>([]);
  const [selectedLevel, setSelectedLevel] = useState<
    'store' | 'city' | 'country' | 'eu' | 'global'
  >(formData?.reachArea || 'store');

  // Levels that cover a whole region, so there is nothing for the brand to
  // pick: "EU + UK" and the new EU-only level both skip the location search.
  const isRegionLevel = (value: string) => value === 'global' || value === 'eu';
  const [suggestions, setSuggestions] = useState<LocationData[]>([]);
  const [errors, setErrors] = useState<
    Partial<Record<keyof FormDataForCreateCampaign, string>>
  >({});
  const [selectedCountry, setSelectedCountry] = useState<string>('');
  const [countrySearchValue, setCountrySearchValue] = useState<string>('');
  const [isCountryDropdownOpen, setIsCountryDropdownOpen] =
    useState<boolean>(false);
  const [isScrollable, setIsScrollable] = useState<boolean>(false);

  const dropdownRef = useRef<HTMLDivElement>(null);
  const { t } = useI18n();

  useEffect(() => {
    if (formData.selectedItems && formData.selectedItems.length > 0) {
      const initialDisplayItems = formData.selectedItems.map((item) => {
        const parsed = parseLocationData(item);
        return {
          displayName: parsed.name,
          displayAddress: parsed.address,
          backendValue: parsed.address,
        };
      });
      setDisplayItems(initialDisplayItems);
    } else {
      setDisplayItems([]);
    }
  }, [formData.selectedItems]);

  const validationSchema = Yup.object({
    reachArea: Yup.string().required(String(t('common.mandatoryField'))),
    selectedItems: Yup.array().when('reachArea', {
      is: (reachArea: string) => !isRegionLevel(reachArea),
      then: (schema) => schema.min(1, String(t('common.selectAtLeastOne'))),
      otherwise: (schema) => schema,
    }),
  });

  const debouncedSearch = useDebounce(searchValue, 500);
  const { data: locationData } = useFetchLocationQuery(
    debouncedSearch,
    selectedLevel,
  );

  useEffect(() => {
    if (!formData.reachArea) {
      handleChange({ reachArea: 'store' });
      setSelectedLevel('store');
    } else {
      setSelectedLevel(formData.reachArea);
    }
  }, [formData.reachArea, handleChange]);

  useEffect(() => {
    if (!debouncedSearch) {
      setSuggestions([]);
      return;
    }
    const parsedSuggestions: LocationData[] =
      locationData?.list?.map((item: { data: string }) =>
        parseLocationData(item.data),
      ) || [];
    setSuggestions(parsedSuggestions);
  }, [locationData, debouncedSearch]);

  const handleRadioChange = (value: string) => {
    setSelectedLevel(value);
    handleChange({ reachArea: value, selectedItems: [] });
    setSearchValue('');
    setSuggestions([]);
    setDisplayItems([]);
    if (isRegionLevel(value)) {
      setSearchValue('');
      handleChange({ selectedItems: [] });
    }
  };

  const selectedScrollRef = useRef<HTMLDivElement>(null);
  const fakeThumbRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const container = selectedScrollRef.current;

    if (!container) return;

    // Check if content is scrollable
    const checkScrollable = () => {
      const scrollable = container.scrollHeight > container.clientHeight;
      setIsScrollable(scrollable);
    };

    const onScroll = () => {
      const thumb = fakeThumbRef.current;
      if (!thumb) return; // Guard only for thumb operations

      const ratio =
        container.scrollTop / (container.scrollHeight - container.clientHeight);

      const maxTranslate = container.clientHeight - thumb.offsetHeight;

      thumb.style.transform = `translateY(${ratio * maxTranslate}px)`;
    };

    checkScrollable();
    onScroll(); // This will now work once thumb exists

    container.addEventListener('scroll', onScroll);

    // Use ResizeObserver to detect content changes
    const resizeObserver = new ResizeObserver(checkScrollable);
    resizeObserver.observe(container);

    return () => {
      container.removeEventListener('scroll', onScroll);
      resizeObserver.disconnect();
    };
  }, [displayItems]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(e.target.value);
  };

  const handleSuggestionClick = (suggestion: LocationData) => {
    const currentBackendItems = formData.selectedItems || [];
    const currentDisplayItems = displayItems;

    if (!currentBackendItems.includes(suggestion.address)) {
      handleChange({
        selectedItems: [...currentBackendItems, suggestion.address],
      });
      setDisplayItems([
        ...currentDisplayItems,
        {
          displayName: suggestion.name,
          displayAddress: suggestion.address,
          backendValue: suggestion.address,
        },
      ]);
      setErrors((prev) => ({ ...prev, selectedItems: undefined }));
    }
    setSearchValue('');
  };

  const handleClearInput = () => {
    setSearchValue('');
  };

  const handleRemoveItem = (backendValue: string) => {
    const currentBackendItems = formData.selectedItems || [];
    handleChange({
      selectedItems: currentBackendItems.filter(
        (item) => item !== backendValue,
      ),
    });
    setDisplayItems(
      displayItems.filter((item) => item.backendValue !== backendValue),
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await validationSchema.validate(formData, { abortEarly: false });
      setErrors({});
      onNext();
    } catch (err) {
      if (err instanceof Yup.ValidationError) {
        const newErrors: Partial<
          Record<keyof FormDataForCreateCampaign, string>
        > = {};
        err.inner.forEach((error) => {
          if (error.path) {
            newErrors[error.path as keyof FormDataForCreateCampaign] =
              error.message;
          }
        });
        setErrors(newErrors);
        toast.error(String(t('campaign.errors.fixErrorsBeforeProceeding')));
      }
    }
  };

  const handleCountrySelect = (country: string) => {
    setSelectedCountry(country);
    setCountrySearchValue('');
    setIsCountryDropdownOpen(false);
    handleChange({ selectedItems: [] });
    setDisplayItems([]);
  };

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsCountryDropdownOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const getFilteredCountries = () => {
    return COUNTRIES.filter((country: { name: string; code: string }) =>
      country.name.toLowerCase().includes(countrySearchValue.toLowerCase()),
    );
  };

  console.log('suggestions', suggestions);

  return (
    <form
      onSubmit={handleSubmit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
        }
      }}
      className="flex flex-col flex-1 min-h-0"
    >
      <div className="flex-1 min-h-0 overflow-y-auto space-y-4 pr-1">
        <div>
          <h3 className="text-base font-semibold text-[#172B4D] mb-8">
            {t('common.selectReachAreaForInStoreCampaign')}{' '}
            <ToolTip
              text={String(t(`common.selectAreaTooltip`))}
              position="top"
            >
              <span>ⓘ</span>
            </ToolTip>
          </h3>
          <div>
            <div className="flex space-x-4 mb-4">
              <CustomRadioButton
                name="reachArea"
                value="store"
                checked={selectedLevel === 'store'}
                onChange={() => handleRadioChange('store')}
                label={t('common.storeLevel')}
              />
              <CustomRadioButton
                name="reachArea"
                value="city"
                checked={selectedLevel === 'city'}
                onChange={() => handleRadioChange('city')}
                label={t('common.cityLevel')}
              />
              <CustomRadioButton
                name="reachArea"
                value="country"
                checked={selectedLevel === 'country'}
                onChange={() => handleRadioChange('country')}
                label={t('common.countryLevel')}
              />
              <CustomRadioButton
                name="reachArea"
                value="eu"
                checked={selectedLevel === 'eu'}
                onChange={() => handleRadioChange('eu')}
                label={t('common.eu')}
              />
              <CustomRadioButton
                name="reachArea"
                value="global"
                checked={selectedLevel === 'global'}
                onChange={() => handleRadioChange('global')}
                label={t('common.eu+uk')}
              />
            </div>
            {errors.reachArea && (
              <div className="text-red-600 text-sm mt-1">
                {errors.reachArea}
              </div>
            )}
          </div>
          {!isRegionLevel(selectedLevel) && (
            <div className="flex gap-4">
              <div className="relative w-80">
                <div className="relative">
                  <input
                    type="text"
                    value={searchValue}
                    onChange={handleInputChange}
                    placeholder={String(
                      t('campaign.placeholders.searchLocation', {
                        level: selectedLevel,
                      }),
                    )}
                    className="placeholder-[#B3B3B2] w-full py-3 px-4 border border-[#B3B3B2] rounded-[32px] focus:outline-none  overflow-ellipsis whitespace-nowrap bg-white focus:border focus:border-[#113E53] text-[#113E53]  text-base leading-[22px] "
                  />
                  {searchValue ? (
                    <button
                      onClick={handleClearInput}
                      className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                    >
                      <img
                        src="/assets/fi_close.svg"
                        className="h-[20px] w-[20px]"
                        alt={String(t('campaign.altTexts.clearSearch'))}
                      />
                    </button>
                  ) : (
                    <img
                      src="/assets/searchIcon.svg"
                      className="h-[20px] w-[20px] absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                      alt={String(t('campaign.altTexts.searchIcon'))}
                    />
                  )}
                </div>
                {searchValue && suggestions.length > 0 && (
                  <div className="suggestions-scroll absolute mt-1 border border-[#787878] rounded-3xl bg-white shadow-lg z-10 p-[5px] max-h-80 overflow-y-auto w-full">
                    <style>
                      {`
                .suggestions-scroll {
                /* Firefox */
                 scrollbar-width: none;
                  }

                 /* Chrome / Edge / Safari */
                .suggestions-scroll::-webkit-scrollbar {
                  display: none;
                    }
                   `}
                    </style>

                    {suggestions.map((suggestion, index) => (
                      <div
                        key={`${suggestion.address}-${index}`}
                        onClick={() => handleSuggestionClick(suggestion)}
                        className={` mb-1 py-2 px-4 hover:bg-gray-100 cursor-pointer flex items-center rounded-3xl w-full ${
                          (formData.selectedItems || []).includes(
                            suggestion.address,
                          )
                            ? 'bg-gray-200'
                            : ''
                        }`}
                      >
                        <img
                          src="/assets/location.svg"
                          alt={String(t('campaign.altTexts.locationIcon'))}
                          className="mr-5 flex-shrink-0"
                        />
                        <div className="flex flex-col w-fit">
                          <span className="text-[#172B4D] font-medium text-base">
                            {suggestion.name}
                          </span>
                          <span className="text-[#4D4D4D] text-base">
                            {suggestion.address}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                {isCountryDropdownOpen && (
                  <div className="absolute w-full mt-1 border border-[#787878] rounded-3xl bg-white shadow-lg z-10 max-h-60 overflow-y-auto">
                    {getFilteredCountries().map(
                      (country: { name: string; code: string }) => (
                        <div
                          key={country.code}
                          className="py-2 px-4 hover:bg-gray-100 cursor-pointer flex items-center"
                          onClick={() => handleCountrySelect(country.name)}
                        >
                          <span className="text-[#172B4D] font-medium">
                            {country.name}
                          </span>
                        </div>
                      ),
                    )}
                    {getFilteredCountries().length === 0 && (
                      <div className="py-2 px-4 text-gray-500">
                        {t('common.no_countries_found')}
                      </div>
                    )}
                  </div>
                )}
              </div>
              <div className="relative w-[420px] max-h-[200px]">
                <div
                  ref={selectedScrollRef}
                  className="selected-locations-scroll max-h-[200px] overflow-y-auto pr-3"
                >
                  {(displayItems || []).map((item, index) => (
                    <div
                      key={`${item.backendValue}-${index}`}
                      className="flex items-start px-3 py-2 rounded-md w-full justify-between mb-2"
                      aria-label={String(
                        t('campaign.labels.selectedLocation', {
                          name: item.displayName,
                          address: item.displayAddress,
                        }),
                      )}
                    >
                      <div className="flex min-w-0">
                        <img
                          src="/assets/world.svg"
                          alt={String(t('campaign.altTexts.worldIcon'))}
                          className="mr-4 h-[24px] w-[24px] mt-[4px] flex-shrink-0"
                        />

                        <div className="flex flex-col min-w-0">
                          <span
                            className="text-[#172B4D] font-medium text-sm truncate cursor-default"
                            title={item.displayName}
                          >
                            {item.displayName}
                          </span>

                          <span
                            className="text-[#4D4D4D] text-sm truncate cursor-default"
                            title={item.displayAddress}
                            style={{
                              display: '-webkit-box',
                              WebkitLineClamp: 1,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden',
                            }}
                          >
                            {item.displayAddress}
                          </span>
                        </div>
                      </div>

                      <button
                        onClick={() => handleRemoveItem(item.backendValue)}
                        className="ml-2 flex-shrink-0"
                        aria-label={`Remove location ${item.displayName}`}
                      >
                        <img
                          src="/assets/fi_close.svg"
                          className="h-[18px] w-[18px]"
                          alt={`Remove ${item.displayName}`}
                        />
                      </button>
                    </div>
                  ))}

                  {errors.selectedItems && (
                    <div className="text-red-600 text-sm mt-1">
                      {errors.selectedItems}
                    </div>
                  )}
                </div>
                {isScrollable && (
                  <div className="fake-scroll-track">
                    <div ref={fakeThumbRef} className="fake-scroll-thumb" />
                  </div>
                )}
                <style>
                  {`
  .selected-locations-scroll {
    scrollbar-width: none; /* Firefox */
  }

  .selected-locations-scroll::-webkit-scrollbar {
    display: none; /* Chrome */
  }

  .fake-scroll-track {
    position: absolute;
    top: 0;
    right: 6px;
    width: 4px;
    height: calc(100% - 16px);
    pointer-events: none;
    margin-bottom: 8px;
  }

  .fake-scroll-thumb {
    width: 100%;
    height: 40px;
    background-color: #C7CCD1;
    border-radius: 9999px;
    transform: translateY(0);
    transition: transform 0.1s linear;
  }
`}
                </style>
              </div>
            </div>
          )}
        </div>
      </div>
      <div className="bg-white flex justify-end w-full shrink-0">
        <div className="flex gap-4 w-full p-6 justify-end">
          <Button
            variant="outlined"
            type="button"
            onClick={onPrevious}
            className="w-[122px]"
          >
            {t('common.previous')}
          </Button>
          <Button type="submit" className="w-[122px]">
            {t('common.next')}
          </Button>
        </div>
      </div>
    </form>
  );
};

export default CreateCampaignStep4;
